'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SMART_PHRASES } from './consts';
import { SmartPhraseInputProps } from './types';

const SmartPhraseInput: FC<SmartPhraseInputProps> = ({
  setActionableInterventionText,
  placeholder = 'Search',
  autoFocus = false,
  onFocus,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredPhrases = useMemo(() => {
    const query = inputValue.toLowerCase().trim();
    const indexed = SMART_PHRASES.map((p, i) => ({ ...p, originalIndex: i }));
    if (!query) return indexed;
    return indexed.filter(
      (p) =>
        p.description.toLowerCase().includes(query) ||
        p.intervention.toLowerCase().includes(query),
    );
  }, [inputValue]);

  const showCreatable = inputValue.trim().length > 0;
  const totalItems = filteredPhrases.length + (showCreatable ? 1 : 0);

  const selectPhrase = useCallback(
    (value: string) => {
      setActionableInterventionText(value);
      setIsOpen(false);
    },
    [setActionableInterventionText],
  );

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredPhrases.length
        ) {
          selectPhrase(filteredPhrases[highlightedIndex].intervention);
        } else if (
          highlightedIndex === filteredPhrases.length &&
          showCreatable
        ) {
          selectPhrase(inputValue.trim());
        } else if (inputValue.trim()) {
          selectPhrase(inputValue.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setHighlightedIndex(-1);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => {
          setIsOpen(true);
          onFocus?.();
        }}
        onBlur={() => {
          // Delay to allow onMouseDown on items to fire first
          setTimeout(() => {
            setIsOpen(false);
            setHighlightedIndex(-1);
          }, 150);
          onBlur?.();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid="smart-phrase-input"
        className="w-full rounded-md border border-primary-400 px-3 py-2 text-sm outline-none bg-white focus:border-black"
      />
      {isOpen && totalItems > 0 && (
        <div
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-[#E2E8F0] bg-white shadow-md"
        >
          {filteredPhrases.map((phrase, i) => (
            <div
              key={`${phrase.description}-${phrase.originalIndex}`}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              role="option"
              aria-selected={highlightedIndex === i}
              data-testid={`smart-phrase-${phrase.originalIndex}`}
              className={`cursor-pointer px-3 py-2 ${highlightedIndex === i ? 'bg-[#ECF3EE]' : ''}`}
              onMouseDown={() => selectPhrase(phrase.intervention)}
              onMouseEnter={() => setHighlightedIndex(i)}
            >
              <div className="flex flex-col items-start text-sm">
                <span className="text-primary-400">{phrase.description}</span>
                <span className="whitespace-pre-line leading-4">
                  {phrase.intervention}
                </span>
              </div>
            </div>
          ))}
          {showCreatable && (
            <div
              ref={(el) => {
                itemRefs.current[filteredPhrases.length] = el;
              }}
              role="option"
              aria-selected={highlightedIndex === filteredPhrases.length}
              data-testid="smart-phrase-custom"
              className={`cursor-pointer px-3 py-2 ${highlightedIndex === filteredPhrases.length ? 'bg-[#ECF3EE]' : ''}`}
              onMouseDown={() => selectPhrase(inputValue.trim())}
              onMouseEnter={() => setHighlightedIndex(filteredPhrases.length)}
            >
              <div className="flex flex-col items-start text-sm">
                <span className="text-primary-400">Custom goal</span>
                <span>{inputValue}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartPhraseInput;
